import { Component, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { DamageAmount, Damage, DamageUserRef } from '../../../../interfaces/Damages.interfaces';
import { DamageManagementService } from '../../../../services/damage-management.service';
import { NotificationService } from '../../../../services/notification.service';

/** Operazioni disponibili sulla pratica danno, una per popup */
export type DamageOperation = 'status' | 'classification' | 'assign' | 'note' | 'amount' | 'amountStatus' | 'amountEdit' | 'amountDelete' | 'close';

export interface DamageOperationMeta {
  key: DamageOperation;
  title: string;
  subtitle: string;
  icon: string;
  /** Tinta della tile icona (header del popup e riquadro nella pagina) */
  tone: 'blue' | 'purple' | 'yellow' | 'green' | 'orange' | 'red';
  submitLabel: string;
  submitIcon: string;
  /** Operazione definitiva: bottone rosso e conferma esplicita */
  danger?: boolean;
  /** Disponibile solo su pratica aperta (stato diverso da CLOSED/REJECTED) */
  requiresOpen?: boolean;
  /** Operazione su una riga (importo): si apre dai bottoni della tabella, non compare tra i riquadri */
  rowOnly?: boolean;
}

/** Metadati delle operazioni, condivisi con la pagina di dettaglio (riquadri della sezione "Operazioni") */
export const DAMAGE_OPERATIONS: DamageOperationMeta[] = [
  { key: 'status',       title: 'Cambio stato',     subtitle: 'Aggiorna lo stato di lavorazione della pratica', icon: 'fa-exchange-alt',        tone: 'blue',   submitLabel: 'Aggiorna stato',   submitIcon: 'fa-check',         requiresOpen: true },
  { key: 'classification', title: 'Classificazione', subtitle: 'Modifica tipo danno, gravità e priorità',       icon: 'fa-layer-group',         tone: 'orange', submitLabel: 'Salva modifiche',  submitIcon: 'fa-check',         requiresOpen: true },
  { key: 'assign',       title: 'Assegnazione',     subtitle: 'Assegna o riassegna la pratica a un operatore',  icon: 'fa-user-check',         tone: 'purple', submitLabel: 'Assegna',          submitIcon: 'fa-user-check',    requiresOpen: true },
  { key: 'note',         title: 'Aggiungi nota',    subtitle: 'Registra una nota nella cronologia',             icon: 'fa-sticky-note',        tone: 'yellow', submitLabel: 'Aggiungi nota',    submitIcon: 'fa-plus' },
  { key: 'amount',       title: 'Nuovo importo',    subtitle: 'Aggiungi un importo economico alla pratica',     icon: 'fa-euro-sign',          tone: 'green',  submitLabel: 'Aggiungi importo', submitIcon: 'fa-plus' },
  { key: 'amountStatus', title: 'Stato importo',    subtitle: 'Aggiorna lo stato di un importo registrato',     icon: 'fa-file-invoice-dollar', tone: 'orange', submitLabel: 'Aggiorna importo', submitIcon: 'fa-check' },
  { key: 'amountEdit',   title: 'Modifica importo', subtitle: 'Correggi i dati di un importo registrato',       icon: 'fa-pen',                tone: 'green',  submitLabel: 'Salva modifiche',  submitIcon: 'fa-check',         rowOnly: true },
  { key: 'amountDelete', title: 'Elimina importo',  subtitle: 'Rimuovi definitivamente un importo registrato',  icon: 'fa-trash-alt',          tone: 'red',    submitLabel: 'Elimina importo',  submitIcon: 'fa-trash-alt',     danger: true, rowOnly: true },
  { key: 'close',        title: 'Chiusura pratica', subtitle: 'Chiudi o respingi definitivamente la pratica',   icon: 'fa-flag-checkered',     tone: 'red',    submitLabel: 'Chiudi pratica',   submitIcon: 'fa-flag-checkered', danger: true, requiresOpen: true }
];

/** Valore numerico positivo (accetta anche la virgola decimale) */
function positiveNumberValidator(control: AbstractControl): ValidationErrors | null {
  const raw = control.value;
  if (raw == null || raw === '') return null;
  const value = Number(String(raw).replace(',', '.'));
  return isNaN(value) || value <= 0 ? { positive: true } : null;
}

/** Intero facoltativo (id addebito) */
function optionalIntegerValidator(control: AbstractControl): ValidationErrors | null {
  const raw = (control.value ?? '').toString().trim();
  return !raw || /^\d+$/.test(raw) ? null : { integer: true };
}

/**
 * Popup delle operazioni sulla pratica danno (/damage-overview/detail): un solo
 * componente che, in base a `operation`, mostra il form di cambio stato,
 * assegnazione, nota, nuovo importo, stato importo o chiusura. Stesso
 * linguaggio dei popup di /dealer-contacts (overlay, header con tile icona,
 * sezioni, footer) e stesse dimensioni per tutte le operazioni.
 * Al salvataggio emette `done`: la pagina ricarica il dettaglio.
 */
@Component({
  selector: 'app-damage-operation-popup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './damage-operation-popup.component.html',
  styleUrls: ['./damage-operation-popup.component.css']
})
export class DamageOperationPopupComponent implements OnChanges {
  @Input() visible = false;
  @Input() operation: DamageOperation | null = null;
  @Input() damage: Damage | null = null;
  /** Importi della pratica (per il popup "Stato importo") */
  @Input() amounts: DamageAmount[] = [];
  /** Importo su cui opera il popup (per "Modifica importo" ed "Elimina importo") */
  @Input() amount: DamageAmount | null = null;
  /** Utenti citati dalla pratica, per mostrare il nome dell'assegnatario corrente */
  @Input() users: Record<string, DamageUserRef> = {};
  @Output() closed = new EventEmitter<void>();
  @Output() done = new EventEmitter<DamageOperation>();

  form: FormGroup;
  isSubmitting = false;

  /** Stati di lavorazione del cambio stato; la chiusura (CLOSED/REJECTED) ha il suo popup */
  readonly workStatusOptions = [
    { value: 'OPEN',        label: 'Aperta' },
    { value: 'IN_PROGRESS', label: 'In lavorazione' },
    { value: 'WAITING',     label: 'In attesa' }
  ];
  readonly closeStatusOptions = [
    { value: 'CLOSED',   label: 'Chiusa' },
    { value: 'REJECTED', label: 'Respinta' }
  ];
  /** Stessi esiti del form di creazione pratica (create-damage) */
  readonly closeOutcomeOptions = ['RESOLVED', 'REFUNDED', 'REPLACED', 'REJECTED'];
  readonly amountTypeOptions = [
    { value: 'REQUESTED',      label: 'Richiesto' },
    { value: 'RECOGNIZED',     label: 'Riconosciuto' },
    { value: 'CARRIER_CHARGE', label: 'Addebito vettore' },
    { value: 'REFUND',         label: 'Rimborso' },
    { value: 'OTHER',          label: 'Altro' }
  ];
  readonly amountStatusOptions = ['PROPOSED', 'APPROVED', 'INVOICED', 'PAID', 'CHARGED', 'CANCELLED'];
  /** Valori dei tipi importo standard (per mostrare in modifica anche un tipo fuori elenco) */
  get amountTypeOptionValues(): string[] {
    return this.amountTypeOptions.map(o => o.value);
  }
  /** Stesse opzioni del form di creazione pratica (create-damage) */
  readonly typeOptions     = ['PHYSICAL', 'WATER', 'BREAKAGE', 'MISSING_PARTS', 'PACKAGING', 'OTHER'];
  readonly severityOptions = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
  readonly priorityOptions = [1, 2, 3, 4, 5];

  constructor(
    private fb: FormBuilder,
    private damageService: DamageManagementService,
    private notificationService: NotificationService
  ) {
    this.form = this.fb.group({});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['visible'] || changes['operation'] || changes['amount']) && this.visible && this.operation) {
      this.form = this.createForm(this.operation);
      this.isSubmitting = false;
    }
  }

  /** Esc chiude il popup (come il click sull'overlay) */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.visible) this.close();
  }

  // ─── Dati per il template ───

  get meta(): DamageOperationMeta {
    return DAMAGE_OPERATIONS.find(o => o.key === this.operation) ?? DAMAGE_OPERATIONS[0];
  }

  get damageLabel(): string {
    if (!this.damage) return '';
    return this.damage.damageCode || ('#' + this.damage.damageId);
  }

  get currentStatus(): string {
    return this.damage?.damageStatus ?? '';
  }

  /** Stati di lavorazione diversi da quello corrente */
  get availableWorkStatuses(): { value: string; label: string }[] {
    return this.workStatusOptions.filter(o => o.value !== this.currentStatus);
  }

  /** Nome dell'assegnatario corrente (codice se non risolto), vuoto se non assegnata */
  get currentAssigneeName(): string {
    const code = this.damage?.damageAssignedTo;
    if (!code) return '';
    return this.users[code]?.userName?.trim() || code;
  }

  /** Valuta della pratica: quella del primo importo, EUR in mancanza */
  get currency(): string {
    return this.amounts.find(a => !!a.damageAmountCurrency)?.damageAmountCurrency ?? 'EUR';
  }

  /** Riga dell'importo nel selettore del popup "Stato importo" */
  amountOptionLabel(amount: DamageAmount): string {
    const value = amount.damageAmountValue != null
      ? Number(amount.damageAmountValue).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '-';
    return `#${amount.damageAmountId} · ${amount.damageAmountType || '-'} · ${value} ${amount.damageAmountCurrency || this.currency} · ${amount.damageAmountStatus || '-'}`;
  }

  /** Tipi disponibili per la classificazione: le opzioni standard più l'eventuale valore corrente non in elenco */
  get availableTypes(): string[] {
    const current = this.damage?.damageType;
    return current && !this.typeOptions.includes(current) ? [current, ...this.typeOptions] : this.typeOptions;
  }

  /** True se il form di classificazione cambia almeno un valore rispetto alla pratica */
  get hasClassificationChanges(): boolean {
    if (this.operation !== 'classification' || !this.damage) return false;
    const v = this.form.getRawValue();
    const formPriority    = v.priority != null && v.priority !== '' ? Number(v.priority) : null;
    const currentPriority = this.damage.damagePriority != null ? Number(this.damage.damagePriority) : null;
    return (v.type ?? '') !== (this.damage.damageType ?? '')
      || (v.severity ?? '') !== (this.damage.damageSeverity ?? '')
      || formPriority !== currentPriority;
  }

  /** Riga descrittiva dell'importo su cui si opera (popup modifica/elimina) */
  get amountLabel(): string {
    return this.amount ? this.amountOptionLabel(this.amount) : '';
  }

  /** True se il form di modifica importo cambia almeno un valore rispetto all'importo */
  get hasAmountChanges(): boolean {
    if (this.operation !== 'amountEdit' || !this.amount) return false;
    const v = this.form.getRawValue();
    const a = this.amount;
    const formValue = Number(String(v.value ?? '').replace(',', '.'));
    return (v.type ?? '') !== (a.damageAmountType ?? '')
      || (!isNaN(formValue) && a.damageAmountValue != null && formValue !== Number(a.damageAmountValue))
      || (a.damageAmountValue == null && String(v.value ?? '').trim() !== '')
      || String(v.currency ?? '').trim().toUpperCase() !== (a.damageAmountCurrency ?? this.currency).toUpperCase()
      || (v.counterpart ?? '').trim() !== (a.damageAmountCounterpart ?? '').trim()
      || (v.counterpartCode ?? '').trim() !== (a.damageAmountCounterpartCode ?? '').trim()
      || (v.amountNote ?? '').trim() !== (a.damageAmountNote ?? '').trim()
      || (v.status ?? '') !== (a.damageAmountStatus ?? '')
      || String(v.chargeId ?? '').trim() !== (a.damageAmountChargeId != null ? String(a.damageAmountChargeId) : '');
  }

  /** Etichetta dello stato di chiusura scelto (per la frase di conferma) */
  get closeStatusLabel(): string {
    const value = this.form.get('status')?.value;
    return this.closeStatusOptions.find(o => o.value === value)?.label ?? value ?? '';
  }

  trackByAmount(index: number, amount: DamageAmount): number {
    return amount.damageAmountId ?? index;
  }

  // ─── Form ───

  private createForm(operation: DamageOperation): FormGroup {
    switch (operation) {
      case 'status':
        return this.fb.group({
          status: ['', Validators.required],
          note:   ['', Validators.maxLength(2000)]
        });
      case 'classification':
        // Precompilato con i valori correnti: si salva solo se almeno uno cambia
        return this.fb.group({
          type:     [this.damage?.damageType ?? '', Validators.required],
          severity: [this.damage?.damageSeverity ?? '', Validators.required],
          priority: [this.damage?.damagePriority ?? null, Validators.required],
          note:     ['', Validators.maxLength(2000)]
        });
      case 'assign':
        return this.fb.group({
          assignedTo: ['', [Validators.required, Validators.maxLength(50)]]
        });
      case 'note':
        return this.fb.group({
          note: ['', [Validators.required, Validators.maxLength(2000)]]
        });
      case 'amount':
        return this.fb.group({
          type:            ['', Validators.required],
          value:           ['', [Validators.required, positiveNumberValidator]],
          currency:        [this.currency, [Validators.required, Validators.pattern(/^[A-Za-z]{3}$/)]],
          counterpart:     ['', Validators.maxLength(200)],
          counterpartCode: ['', Validators.maxLength(50)],
          note:            ['', Validators.maxLength(2000)]
        });
      case 'amountStatus':
        // Preselezionato se aperto dal bottone della riga (amount) o se la pratica ha un solo importo
        return this.fb.group({
          amountId: [this.amount?.damageAmountId ?? (this.amounts.length === 1 ? this.amounts[0].damageAmountId : null), Validators.required],
          status:   ['', Validators.required],
          chargeId: ['', optionalIntegerValidator]
        });
      case 'amountEdit': {
        // Precompilato con i valori correnti dell'importo: si salva solo se almeno uno cambia
        const a = this.amount;
        return this.fb.group({
          type:            [a?.damageAmountType ?? '', Validators.required],
          value:           [a?.damageAmountValue != null ? this.limitDecimals(String(a.damageAmountValue).replace('.', ',')) : '', [Validators.required, positiveNumberValidator]],
          currency:        [a?.damageAmountCurrency ?? this.currency, [Validators.required, Validators.pattern(/^[A-Za-z]{3}$/)]],
          counterpart:     [a?.damageAmountCounterpart ?? '', Validators.maxLength(200)],
          counterpartCode: [a?.damageAmountCounterpartCode ?? '', Validators.maxLength(50)],
          amountNote:      [a?.damageAmountNote ?? '', Validators.maxLength(2000)],
          status:          [a?.damageAmountStatus ?? '', Validators.required],
          chargeId:        [a?.damageAmountChargeId != null ? String(a.damageAmountChargeId) : '', optionalIntegerValidator],
          note:            ['', Validators.maxLength(2000)]
        });
      }
      case 'amountDelete':
        return this.fb.group({
          note:    ['', Validators.maxLength(2000)],
          confirm: [false, Validators.requiredTrue]
        });
      case 'close':
        return this.fb.group({
          status:  ['CLOSED', Validators.required],
          outcome: [''],
          note:    ['', Validators.maxLength(2000)],
          confirm: [false, Validators.requiredTrue]
        });
    }
  }

  /**
   * Mentre si digita il valore, tiene solo cifre e un separatore decimale (punto o
   * virgola) e tronca a 2 decimali: il campo non accetta più di due cifre dopo la virgola.
   */
  onValueInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const limited = this.limitDecimals(input.value);
    if (limited !== input.value) {
      input.value = limited;
    }
    this.form.get('value')?.setValue(limited, { emitEvent: false });
  }

  /** Normalizza un testo numerico: solo cifre, un separatore decimale, al massimo 2 decimali */
  private limitDecimals(raw: string): string {
    const cleaned = String(raw ?? '').replace(/[^\d.,]/g, '');
    const match = cleaned.match(/^(\d*)([.,]?)(\d*)/);
    if (!match) return '';
    const [, intPart, sep, decPart] = match;
    return sep ? `${intPart}${sep}${decPart.slice(0, 2)}` : intPart;
  }

  isInvalid(name: string): boolean {
    const control = this.form.get(name);
    return !!control && control.invalid && control.touched;
  }

  get canSubmit(): boolean {
    if (this.operation === 'classification' && !this.hasClassificationChanges) return false;
    if (this.operation === 'amountEdit' && !this.hasAmountChanges) return false;
    if ((this.operation === 'amountEdit' || this.operation === 'amountDelete') && !this.amount) return false;
    return this.form.valid && !this.isSubmitting;
  }

  close(): void {
    if (!this.isSubmitting) {
      this.closed.emit();
    }
  }

  submit(): void {
    if (!this.damage || !this.operation) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v  = this.form.getRawValue();
    const id = this.damage.damageId;
    let request$: Observable<unknown>;
    let successMessage: string;

    switch (this.operation) {
      case 'status':
        request$ = this.damageService.changeDamageStatus(id, v.status, v.note?.trim() || null);
        successMessage = 'Stato della pratica aggiornato.';
        break;
      case 'classification':
        request$ = this.damageService.updateDamageClassification(id, {
          type:     v.type,
          severity: v.severity,
          priority: v.priority != null && v.priority !== '' ? Number(v.priority) : null,
          note:     v.note?.trim() || null
        });
        successMessage = 'Classificazione della pratica aggiornata.';
        break;
      case 'assign':
        request$ = this.damageService.assignDamage(id, v.assignedTo.trim());
        successMessage = `Pratica assegnata a ${v.assignedTo.trim()}.`;
        break;
      case 'note':
        request$ = this.damageService.addDamageNote(id, v.note.trim());
        successMessage = 'Nota aggiunta alla cronologia.';
        break;
      case 'amount': {
        const amount: Partial<DamageAmount> = {
          damageAmountType:     v.type,
          damageAmountValue:    Number(String(v.value).replace(',', '.')),
          damageAmountCurrency: String(v.currency).trim().toUpperCase()
        };
        if (v.counterpart?.trim())     amount.damageAmountCounterpart     = v.counterpart.trim();
        if (v.counterpartCode?.trim()) amount.damageAmountCounterpartCode = v.counterpartCode.trim();
        if (v.note?.trim())            amount.damageAmountNote            = v.note.trim();
        request$ = this.damageService.addDamageAmount(id, amount);
        successMessage = 'Importo aggiunto alla pratica.';
        break;
      }
      case 'amountStatus': {
        const chargeText = (v.chargeId ?? '').toString().trim();
        request$ = this.damageService.updateDamageAmountStatus(Number(v.amountId), v.status, chargeText ? Number(chargeText) : null);
        successMessage = 'Stato dell\'importo aggiornato.';
        break;
      }
      case 'amountEdit': {
        const amountId = this.amount!.damageAmountId;
        request$ = this.damageService.updateDamageAmount(amountId, {
          type:            v.type,
          value:           Number(String(v.value).replace(',', '.')),
          currency:        String(v.currency).trim().toUpperCase(),
          counterpart:     (v.counterpart ?? '').trim(),
          counterpartCode: (v.counterpartCode ?? '').trim(),
          amountNote:      (v.amountNote ?? '').trim(),
          status:          v.status,
          chargeId:        String(v.chargeId ?? '').trim() ? Number(String(v.chargeId).trim()) : null,
          note:            v.note?.trim() || null
        });
        successMessage = `Importo #${amountId} aggiornato.`;
        break;
      }
      case 'amountDelete': {
        const amountId = this.amount!.damageAmountId;
        request$ = this.damageService.deleteDamageAmount(amountId, v.note?.trim() || null);
        successMessage = `Importo #${amountId} eliminato.`;
        break;
      }
      case 'close':
        request$ = this.damageService.closeDamage(id, v.status, v.outcome || null, v.note?.trim() || null);
        successMessage = 'Pratica chiusa.';
        break;
    }

    this.isSubmitting = true;
    request$.pipe(
      finalize(() => { this.isSubmitting = false; })
    ).subscribe({
      next: () => {
        this.notificationService.success(successMessage);
        this.done.emit(this.operation!);
      },
      error: (err: Error) => {
        this.notificationService.error(err?.message || 'Operazione non riuscita. Riprova più tardi.');
      }
    });
  }
}
